import { useEffect, useRef } from "react";

/**
 * The find band over the open log (日本-603, canvas https://claude.ai/artifact/6Lhtzik8yv4VJfVrpWQvPY,
 * frames A–C).
 *
 * **Nothing here is under the 14px type floor**, where the canvas drew the count and the key chips
 * at 12–13px: `check-grammar.mjs` treats a sub-floor span as a defect, and the floor wins.
 *
 * **The field owns every key that matters here, through React's own `onKeyDown`** — Read's window
 * listener already returns for a text entry, so nothing in the log's ladder competes with it. The
 * one guard that is load-bearing rather than tidy is `isComposing`: a Japanese IME confirms a
 * conversion with Enter, and without the guard typing 共に would jump to the next hit halfway
 * through converting it.
 *
 * `↑`/`↓` in the field step between hits as well as Enter/Shift+Enter, deliberately (Alex): the
 * field is one line, so the arrows had nothing else to do there.
 */
export function FindBand({
  query,
  opened,
  count,
  current,
  onQuery,
  onStep,
  onClose,
  isPhone,
}: {
  query: string;
  /** `useLogFind`'s counter: a new value means Ctrl+F was pressed again. */
  opened: number;
  count: number;
  current: number | null;
  onQuery: (query: string) => void;
  onStep: (dir: 1 | -1) => void;
  onClose: () => void;
  isPhone: boolean;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Opening puts the caret in the field with the last query selected, so typing replaces it and
  // Enter re-runs it — the browser's own find does both.
  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [opened]);

  const miss = query.trim() !== "" && count === 0;
  const stepsOff = count === 0;

  const countLabel = miss ? (
    <span data-check="read.find-count" className="text-[color:var(--accent-text)]">
      一致なし
    </span>
  ) : count > 0 ? (
    <span data-check="read.find-count">
      <b className="font-semibold text-ink">{current === null ? 0 : current + 1}</b> / {count}件
    </span>
  ) : null;

  const field = (
    <input
      ref={inputRef}
      data-check="read.find-input"
      value={query}
      onChange={(event) => onQuery(event.target.value)}
      onKeyDown={(event) => {
        // keyCode 229 is the IME's own keydown in older WebViews, which do not set `isComposing`.
        if (event.nativeEvent.isComposing || event.keyCode === 229) return;
        if (event.key === "Enter" || event.key === "ArrowDown" || event.key === "ArrowUp") {
          event.preventDefault();
          const back = event.key === "ArrowUp" || (event.key === "Enter" && event.shiftKey);
          onStep(back ? -1 : 1);
        } else if (event.key === "Escape") {
          event.preventDefault();
          onClose();
        }
      }}
      placeholder="このログを検索"
      aria-label="このログを検索"
      enterKeyHint="search"
      // `text`, not `search`: Chrome draws its own clear ✕ inside a search field, which the canvas
      // does not have and which sits beside the band's own × meaning something else.
      type="text"
      className={[
        "min-w-0 flex-1 rounded-[var(--r-2)] border bg-void px-[8px] font-jp text-[16px] text-ink outline-none placeholder:text-ink-4",
        isPhone ? "h-9" : "h-7 max-w-[420px]",
        miss
          ? "border-[color:var(--accent-line)]"
          : "border-[color:var(--line-strong)] focus:border-[color:var(--accent-focus)] focus:shadow-[0_0_0_1px_var(--accent-focus)]",
      ].join(" ")}
    />
  );

  const stepButton = (dir: 1 | -1) => (
    <button
      type="button"
      disabled={stepsOff}
      onClick={() => onStep(dir)}
      aria-label={dir === 1 ? "次の一致" : "前の一致"}
      className={[
        "flex shrink-0 items-center justify-center rounded-[var(--r-2)] border font-jp text-[14px]/[1]",
        isPhone ? "h-9 w-11" : "h-7 w-7",
        stepsOff
          ? "border-[color:var(--line)] text-ink-off"
          : "border-[color:var(--line-strong)] text-ink-3 hover:text-ink",
      ].join(" ")}
    >
      {dir === 1 ? "↓" : "↑"}
    </button>
  );

  const close = (
    <button
      type="button"
      onClick={onClose}
      aria-label="検索を閉じる"
      className="shrink-0 px-[4px] text-[16px]/[1] text-ink-3 hover:text-ink"
    >
      {isPhone ? <span className="text-meta text-ink-4">閉じる ×</span> : "×"}
    </button>
  );

  if (isPhone) {
    return (
      <div role="search" data-check="read.find" className="shrink-0 border-b border-[color:var(--line)] bg-sunken">
        <div className="flex items-center gap-[6px] py-[8px] pl-4 pr-[10px]">
          {field}
          {stepButton(-1)}
          {stepButton(1)}
        </div>
        <div className="flex items-center justify-between px-4 pb-[8px] font-mono-jp text-meta text-ink-3 tabular-nums">
          <span>{countLabel}</span>
          {close}
        </div>
      </div>
    );
  }

  return (
    <div
      role="search"
      data-check="read.find"
      className="flex h-[42px] shrink-0 items-center gap-[10px] border-b border-[color:var(--line)] bg-sunken pl-[18px] pr-[10px]"
    >
      <span aria-hidden className="font-jp text-[14px] font-semibold text-ink-4">
        検
      </span>
      {field}
      <span className="min-w-[74px] font-mono-jp text-meta text-ink-3 tabular-nums">{countLabel}</span>
      {stepButton(-1)}
      {stepButton(1)}
      <span className="ml-auto flex gap-[10px] whitespace-nowrap text-meta text-ink-4">
        <span><Key>Enter</Key> 次</span>
        <span><Key>Shift+Enter</Key> 前</span>
        <span><Key>Esc</Key> 閉じる</span>
      </span>
      {close}
    </div>
  );
}

function Key({ children }: { children: string }) {
  return (
    <span className="rounded-[var(--r-2)] border border-[color:var(--line-strong)] bg-raised px-[5px] py-[1px] font-mono text-meta font-semibold text-ink-3">
      {children}
    </span>
  );
}
